/**
 * Astrological Calculations & Astronomical Mathematics
 * 
 * Real astronomical calculations based on:
 * - IAU (International Astronomical Union) standards
 * - NASA JPL ephemeris data
 * - VSOP87 theory for planetary positions
 * - Traditional astrological systems (Tropical & Sidereal)
 * 
 * Important distinctions:
 * - **Astronomy**: Science of celestial mechanics (verifiable)
 * - **Astrology**: Interpretation of celestial influence (not scientifically validated)
 * 
 * This file provides:
 * 1. Accurate astronomical calculations (verifiable)
 * 2. Traditional astrological correspondences (cultural/historical)
 * 3. Honest disclaimers about scientific status
 * 
 * Sources:
 * - Meeus, J. (1998). "Astronomical Algorithms" - Standard reference
 * - NASA JPL Horizons System
 * - Swiss Ephemeris
 * - Traditional astrological texts (Ptolemy, Vettius Valens, Parashara)
 */

// ============================================================================
// ZODIAC SYSTEM - Astronomical & Astrological
// ============================================================================

/**
 * Zodiac Signs with complete data
 * 
 * Tropical Zodiac: Based on seasons (vernal equinox at 0° Aries)
 * Sidereal Zodiac: Based on constellations (currently about 24° offset due to precession)
 * 
 * Western astrology uses Tropical.
 * Vedic astrology uses Sidereal.
 */
export const ZODIAC_SIGNS = {
  aries: {
    name: 'Aries',
    symbol: '♈',
    element: 'Fire',
    modality: 'Cardinal',
    ruler: 'Mars',
    exaltation: 'Sun',
    detriment: 'Venus',
    fall: 'Saturn',
    tropicalDates: 'March 21 - April 19',
    tropicalDegrees: '0° - 30°',
    sanskrit: 'Mesha (मेष)',
    vedic: {
      ruler: 'Mangala (Mars)',
      nature: 'Movable (Chara)',
    },
    qualities: ['Initiative', 'Courage', 'Leadership', 'Impulsiveness'],
    anatomy: 'Head, face, brain',
    constellation: {
      mainStars: ['Hamal (α Ari)', 'Sheratan (β Ari)', 'Mesarthim (γ Ari)'],
      brightest: 'Hamal (magnitude 2.0)',
    },
  },
  
  taurus: {
    name: 'Taurus',
    symbol: '♉',
    element: 'Earth',
    modality: 'Fixed',
    ruler: 'Venus',
    exaltation: 'Moon',
    detriment: 'Mars',
    fall: 'none',
    tropicalDates: 'April 20 - May 20',
    tropicalDegrees: '30° - 60°',
    sanskrit: 'Vrishabha (वृषभ)',
    qualities: ['Stability', 'Patience', 'Sensuality', 'Stubbornness'],
    anatomy: 'Throat, neck, thyroid',
    constellation: {
      mainStars: ['Aldebaran (α Tau)', 'Elnath (β Tau)'],
      brightest: 'Aldebaran (magnitude 0.85)',
      cluster: 'Pleiades (M45) - visible to naked eye',
    },
  },
  
  gemini: {
    name: 'Gemini',
    symbol: '♊',
    element: 'Air',
    modality: 'Mutable',
    ruler: 'Mercury',
    exaltation: 'none',
    detriment: 'Jupiter',
    fall: 'none',
    tropicalDates: 'May 21 - June 20',
    tropicalDegrees: '60° - 90°',
    sanskrit: 'Mithuna (मिथुन)',
    qualities: ['Communication', 'Adaptability', 'Curiosity', 'Duality'],
    anatomy: 'Arms, shoulders, hands, lungs',
    constellation: {
      mainStars: ['Pollux (β Gem)', 'Castor (α Gem)'],
      mythology: 'The Twins - Castor and Pollux',
    },
  },
  
  cancer: {
    name: 'Cancer',
    symbol: '♋',
    element: 'Water',
    modality: 'Cardinal',
    ruler: 'Moon',
    exaltation: 'Jupiter',
    detriment: 'Saturn',
    fall: 'Mars',
    tropicalDates: 'June 21 - July 22',
    tropicalDegrees: '90° - 120°',
    sanskrit: 'Karka (कर्क)',
    qualities: ['Nurturing', 'Emotional', 'Intuitive', 'Protective'],
    anatomy: 'Chest, breasts, stomach',
    constellation: {
      cluster: 'Beehive Cluster (M44) - Praesepe',
      visibility: 'Visible to naked eye in dark skies',
    },
  },
  
  leo: {
    name: 'Leo',
    symbol: '♌',
    element: 'Fire',
    modality: 'Fixed',
    ruler: 'Sun',
    exaltation: 'none',
    detriment: 'Saturn',
    fall: 'none',
    tropicalDates: 'July 23 - August 22',
    tropicalDegrees: '120° - 150°',
    sanskrit: 'Simha (सिंह)',
    qualities: ['Confidence', 'Creativity', 'Generosity', 'Pride'],
    anatomy: 'Heart, spine, upper back',
    constellation: {
      mainStars: ['Regulus (α Leo)', 'Denebola (β Leo)'],
      brightest: 'Regulus (magnitude 1.4) - "Heart of the Lion"',
    },
  },
  
  virgo: {
    name: 'Virgo',
    symbol: '♍',
    element: 'Earth',
    modality: 'Mutable',
    ruler: 'Mercury',
    exaltation: 'Mercury',
    detriment: 'Jupiter',
    fall: 'Venus',
    tropicalDates: 'August 23 - September 22',
    tropicalDegrees: '150° - 180°',
    sanskrit: 'Kanya (कन्या)',
    qualities: ['Analysis', 'Service', 'Precision', 'Criticism'],
    anatomy: 'Digestive system, intestines',
    constellation: {
      mainStars: ['Spica (α Vir)'],
      brightest: 'Spica (magnitude 1.0)',
      cluster: 'Virgo Cluster of galaxies',
    },
  },
  
  libra: {
    name: 'Libra',
    symbol: '♎',
    element: 'Air',
    modality: 'Cardinal',
    ruler: 'Venus',
    exaltation: 'Saturn',
    detriment: 'Mars',
    fall: 'Sun',
    tropicalDates: 'September 23 - October 22',
    tropicalDegrees: '180° - 210°',
    sanskrit: 'Tula (तुला)',
    qualities: ['Balance', 'Harmony', 'Justice', 'Indecision'],
    anatomy: 'Kidneys, lower back',
    constellation: {
      mainStars: ['Zubeneschamali (β Lib)', 'Zubenelgenubi (α Lib)'],
    },
  },
  
  scorpio: {
    name: 'Scorpio',
    symbol: '♏',
    element: 'Water',
    modality: 'Fixed',
    ruler: 'Pluto (modern), Mars (traditional)',
    exaltation: 'none',
    detriment: 'Venus',
    fall: 'Moon',
    tropicalDates: 'October 23 - November 21',
    tropicalDegrees: '210° - 240°',
    sanskrit: 'Vrishchika (वृश्चिक)',
    qualities: ['Intensity', 'Transformation', 'Depth', 'Secrecy'],
    anatomy: 'Reproductive organs, excretory system',
    constellation: {
      mainStars: ['Antares (α Sco) - "Rival of Mars"'],
      brightest: 'Antares (magnitude 1.1, red supergiant)',
    },
  },
  
  sagittarius: {
    name: 'Sagittarius',
    symbol: '♐',
    element: 'Fire',
    modality: 'Mutable',
    ruler: 'Jupiter',
    exaltation: 'none',
    detriment: 'Mercury',
    fall: 'none',
    tropicalDates: 'November 22 - December 21',
    tropicalDegrees: '240° - 270°',
    sanskrit: 'Dhanu (धनु)',
    qualities: ['Optimism', 'Philosophy', 'Adventure', 'Excess'],
    anatomy: 'Hips, thighs, liver',
    constellation: {
      location: 'Galactic center direction',
      nebula: 'Lagoon Nebula (M8), Trifid Nebula (M20)',
    },
  },
  
  capricorn: {
    name: 'Capricorn',
    symbol: '♑',
    element: 'Earth',
    modality: 'Cardinal',
    ruler: 'Saturn',
    exaltation: 'Mars',
    detriment: 'Moon',
    fall: 'Jupiter',
    tropicalDates: 'December 22 - January 19',
    tropicalDegrees: '270° - 300°',
    sanskrit: 'Makara (मकर)',
    qualities: ['Ambition', 'Discipline', 'Responsibility', 'Pessimism'],
    anatomy: 'Bones, knees, skin',
    constellation: {
      faint: 'Relatively faint constellation',
      mythology: 'Sea-goat (goat-fish hybrid)',
    },
  },
  
  aquarius: {
    name: 'Aquarius',
    symbol: '♒',
    element: 'Air',
    modality: 'Fixed',
    ruler: 'Uranus (modern), Saturn (traditional)',
    exaltation: 'none',
    detriment: 'Sun',
    fall: 'none',
    tropicalDates: 'January 20 - February 18',
    tropicalDegrees: '300° - 330°',
    sanskrit: 'Kumbha (कुम्भ)',
    qualities: ['Innovation', 'Humanitarianism', 'Independence', 'Detachment'],
    anatomy: 'Circulatory system, ankles',
    constellation: {
      nebula: 'Helix Nebula (NGC 7293) - "Eye of God"',
    },
  },
  
  pisces: {
    name: 'Pisces',
    symbol: '♓',
    element: 'Water',
    modality: 'Mutable',
    ruler: 'Neptune (modern), Jupiter (traditional)',
    exaltation: 'Venus',
    detriment: 'Mercury',
    fall: 'Mercury',
    tropicalDates: 'February 19 - March 20',
    tropicalDegrees: '330° - 360°/0°',
    sanskrit: 'Meena (मीन)',
    qualities: ['Compassion', 'Imagination', 'Intuition', 'Escapism'],
    anatomy: 'Feet, immune system',
    constellation: {
      location: 'Contains vernal equinox point (slowly precessing)',
    },
  },
} as const;

// ============================================================================
// ASTRONOMICAL CALCULATIONS
// ============================================================================

/**
 * Calculate Julian Day Number
 * 
 * Standard astronomical dating system used for calculations.
 * January 1, 2000, 12:00 TT = JD 2451545.0
 * 
 * Formula from Meeus, "Astronomical Algorithms" (1998)
 * 
 * NOTE: This function now uses UTC for consistency with astronomical calculations.
 * For backward compatibility, it converts local time to UTC if needed.
 * 
 * @param date JavaScript Date object (will be converted to UTC)
 * @returns Julian Day Number
 */
export function calculateJulianDay(date: Date): number {
  // Use UTC components for astronomical accuracy
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1;
  const day = date.getUTCDate();
  const hour = date.getUTCHours();
  const minute = date.getUTCMinutes();
  const second = date.getUTCSeconds();
  const millisecond = date.getUTCMilliseconds();
  
  const decimalDay = day + hour / 24 + minute / 1440 + second / 86400 + millisecond / 86400000;
  
  let y = year;
  let m = month;
  
  if (month <= 2) {
    y = year - 1;
    m = month + 12;
  }
  
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  
  const jd = Math.floor(365.25 * (y + 4716)) + 
             Math.floor(30.6001 * (m + 1)) + 
             decimalDay + b - 1524.5;
  
  return jd;
}

/**
 * Calculate Sun's position (VSOP87-based, accurate)
 * 
 * VSOP87-based calculation with professional-grade accuracy.
 * For maximum precision, use Swiss Ephemeris or NASA JPL data
 * 
 * Based on VSOP87 (Variations Séculaires des Orbites Planétaires)
 * Accuracy: ±0.01° for dates near J2000.0
 * 
 * @param date Date to calculate for (will be converted to UTC)
 * @returns Ecliptic longitude of Sun in degrees (0-360)
 */
export function calculateSunPosition(date: Date): number {
  // Ensure UTC
  const utcDate = new Date(date.toISOString());
  const jd = calculateJulianDay(utcDate);
  const T = (jd - 2451545.0) / 36525.0; // Julian centuries since J2000.0
  
  // Mean longitude of Sun (VSOP87)
  const L0 = (280.4664567 + 36000.76982779 * T + 0.0003032028 * T * T) % 360;
  
  // Mean anomaly (VSOP87)
  const M = (357.5291092 + 35999.0502909 * T - 0.0001537 * T * T) % 360;
  const MRad = M * Math.PI / 180;
  
  // Equation of center (VSOP87 terms with time-dependent coefficients)
  const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(MRad) +
            (0.019993 - 0.000101 * T) * Math.sin(2 * MRad) +
            0.000289 * Math.sin(3 * MRad);
  
  // True longitude
  let lambda = (L0 + C) % 360;
  
  // Normalize to 0-360
  if (lambda < 0) lambda += 360;
  
  return lambda;
}

/**
 * Get zodiac sign from ecliptic longitude
 * 
 * Tropical zodiac: 0° = Aries point (vernal equinox)
 * Each sign = 30° of ecliptic
 * 
 * @param longitude Ecliptic longitude in degrees (0-360)
 * @returns Zodiac sign information
 */
/**
 * Get planet information including glyph and color
 */
export function getPlanetInfo(planetName: string): { symbol: string; color: string } {
  const planet = PLANETARY_CORRESPONDENCES[planetName as keyof typeof PLANETARY_CORRESPONDENCES];
  return planet ? { symbol: planet.symbol, color: planet.color } : { symbol: '●', color: '#FFFFFF' };
}

/**
 * Get zodiac sign color based on element
 */
export function getZodiacColor(signName: string): string {
  const sign = ZODIAC_SIGNS[signName.toLowerCase() as keyof typeof ZODIAC_SIGNS];
  if (!sign) return '#8B5CF6';
  
  // Color based on element
  const elementColors: Record<string, string> = {
    'Fire': '#EF4444',      // Red - passion, energy
    'Earth': '#84CC16',     // Green - stability, growth
    'Air': '#3B82F6',       // Blue - intellect, communication
    'Water': '#06B6D4',     // Cyan - emotion, intuition
  };
  
  return elementColors[sign.element] || '#8B5CF6';
}

/**
 * Get house color based on angular/succedent/cadent nature
 */
export function getHouseColor(houseNumber: number): string {
  // Angular houses (1, 4, 7, 10) - most powerful
  if ([1, 4, 7, 10].includes(houseNumber)) {
    return '#8B5CF6'; // Purple - power, transformation
  }
  // Succedent houses (2, 5, 8, 11) - fixed/stable
  if ([2, 5, 8, 11].includes(houseNumber)) {
    return '#F59E0B'; // Amber - resources, stability
  }
  // Cadent houses (3, 6, 9, 12) - mutable/transitional
  return '#10B981'; // Emerald - learning, adaptation
}

/**
 * Format decimal degrees into traditional astrology format (degrees and minutes)
 * 
 * In astrology, positions are shown as degrees and minutes:
 * - 1 degree = 60 minutes (arcminutes)
 * - Example: 19.75° = 19°45'
 * 
 * @param decimalDegree - Decimal degree value (e.g., 19.75)
 * @returns Formatted string (e.g., "19°45'")
 */
export function formatAstrologyDegree(decimalDegree: number): string {
  const degrees = Math.floor(decimalDegree);
  const minutes = Math.round((decimalDegree - degrees) * 60);
  
  // Handle edge case where rounding gives 60 minutes
  if (minutes === 60) {
    return `${degrees + 1}°00'`;
  }
  
  return `${degrees}°${minutes.toString().padStart(2, '0')}'`;
}

export function getZodiacSign(longitude: number): {
  sign: string;
  degree: number;
  symbol: string;
  element: string;
  modality: string;
} {
  const normalizedLong = ((longitude % 360) + 360) % 360;
  const signIndex = Math.floor(normalizedLong / 30);
  const degreeInSign = normalizedLong % 30;
  
  const signs = [
    'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
    'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces'
  ];
  
  const signKey = signs[signIndex] as keyof typeof ZODIAC_SIGNS;
  const signData = ZODIAC_SIGNS[signKey];
  
  return {
    sign: signData.name,
    degree: degreeInSign, // Keep full decimal precision
    symbol: signData.symbol,
    element: signData.element,
    modality: signData.modality,
  };
}

/**
 * Calculate current astrological "weather"
 * 
 * Returns current Sun sign and useful astronomical data
 * 
 * Note: This is simplified calculation. For birth charts or precision,
 * use professional ephemeris software (Swiss Ephemeris, Astrodienst).
 */
export function getCurrentAstrologicalData(date: Date = new Date()) {
  const sunLongitude = calculateSunPosition(date);
  const sunSign = getZodiacSign(sunLongitude);
  const jd = calculateJulianDay(date);
  
  return {
    date: date.toISOString(),
    julianDay: jd,
    sunPosition: {
      eclipticLongitude: sunLongitude,
      sign: sunSign.sign,
      degree: sunSign.degree,
      notation: `${sunSign.symbol} ${sunSign.degree}°`,
      element: sunSign.element,
      modality: sunSign.modality,
    },
    calculation: {
      method: 'Simplified VSOP87',
      accuracy: '±0.01° (educational approximation)',
      forPrecision: 'Use Swiss Ephemeris or NASA JPL Horizons',
    },
    astronomicalNote: 'Sun position is verifiable astronomical fact',
    astrologicalNote: 'Interpretation of influence is cultural tradition, not science',
  };
}

// ============================================================================
// LUNAR PHASE CALCULATION
// ============================================================================

/**
 * Calculate Moon phase
 * 
 * Simplified calculation accurate to ~1-2 days
 * Based on synodic month (new moon to new moon) = 29.53059 days
 * 
 * Formula from Meeus, "Astronomical Algorithms"
 * 
 * @param date Date to calculate for
 * @returns Moon phase information
 */
export function calculateMoonPhase(date: Date) {
  const jd = calculateJulianDay(date);
  
  // Known new moon: January 6, 2000
  const knownNewMoon = 2451550.1;
  const synodicMonth = 29.53059; // days
  
  // Days since known new moon
  const daysSinceNew = jd - knownNewMoon;
  
  // Current position in cycle (0-1)
  const phase = (daysSinceNew % synodicMonth) / synodicMonth;
  
  // Age in days
  const ageInDays = daysSinceNew % synodicMonth;
  
  // Illumination percentage (0-100)
  const illumination = Math.round((1 - Math.cos(phase * 2 * Math.PI)) * 50);
  
  // Phase name
  let phaseName = '';
  let emoji = '';
  
  if (phase < 0.033) {
    phaseName = 'New Moon';
    emoji = '🌑';
  } else if (phase < 0.216) {
    phaseName = 'Waxing Crescent';
    emoji = '🌒';
  } else if (phase < 0.283) {
    phaseName = 'First Quarter';
    emoji = '🌓';
  } else if (phase < 0.466) {
    phaseName = 'Waxing Gibbous';
    emoji = '🌔';
  } else if (phase < 0.533) {
    phaseName = 'Full Moon';
    emoji = '🌕';
  } else if (phase < 0.716) {
    phaseName = 'Waning Gibbous';
    emoji = '🌖';
  } else if (phase < 0.783) {
    phaseName = 'Last Quarter';
    emoji = '🌗';
  } else {
    phaseName = 'Waning Crescent';
    emoji = '🌘';
  }
  
  return {
    phaseName,
    emoji,
    ageInDays: ageInDays.toFixed(2),
    illumination,
    phaseValue: phase,
    nextNewMoon: calculateNextPhase(jd, 0),
    nextFullMoon: calculateNextPhase(jd, 0.5),
    calculation: {
      synodicMonth: `${synodicMonth} days`,
      knownNewMoon: 'January 6, 2000 (JD 2451550.1)',
      accuracy: '±1-2 days (educational approximation)',
    },
  };
}

/**
 * Calculate next occurrence of a moon phase
 */
function calculateNextPhase(currentJD: number, targetPhase: number): string {
  const synodicMonth = 29.53059;
  const knownNewMoon = 2451550.1;
  
  const daysSinceNew = currentJD - knownNewMoon;
  const currentPhase = (daysSinceNew % synodicMonth) / synodicMonth;
  
  let daysToPhase = 0;
  if (currentPhase < targetPhase) {
    daysToPhase = (targetPhase - currentPhase) * synodicMonth;
  } else {
    daysToPhase = (1 - currentPhase + targetPhase) * synodicMonth;
  }
  
  const nextPhaseJD = currentJD + daysToPhase;
  const nextDate = julianDayToDate(nextPhaseJD);
  
  return nextDate.toLocaleDateString();
}

/**
 * Convert Julian Day to JavaScript Date
 */
function julianDayToDate(jd: number): Date {
  const a = jd + 0.5;
  const z = Math.floor(a);
  const f = a - z;
  
  let A = z;
  if (z >= 2299161) {
    const alpha = Math.floor((z - 1867216.25) / 36524.25);
    A = z + 1 + alpha - Math.floor(alpha / 4);
  }
  
  const B = A + 1524;
  const C = Math.floor((B - 122.1) / 365.25);
  const D = Math.floor(365.25 * C);
  const E = Math.floor((B - D) / 30.6001);
  
  const day = B - D - Math.floor(30.6001 * E) + f;
  const month = E < 14 ? E - 1 : E - 13;
  const year = month > 2 ? C - 4716 : C - 4715;
  
  const date = new Date(year, month - 1, Math.floor(day));
  const milliseconds = (day - Math.floor(day)) * 86400000;
  date.setTime(date.getTime() + milliseconds);
  
  return date;
}

// ============================================================================
// SABIAN SYMBOLS (360 Degrees of Zodiac)
// ============================================================================

/**
 * Sabian Symbols
 * 
 * Created by: Marc Edmund Jones and Elsie Wheeler
 * Date: 1925, San Diego, California
 * Method: Clairvoyant channeling
 * 
 * 360 symbolic images, one for each degree of the zodiac
 * Each degree from Aries 1° to Pisces 30° has a symbolic image and meaning
 * 
 * Scientific status: Esoteric/symbolic system, not astronomically based
 * Cultural significance: Widely used in modern astrology
 * 
 * Full Sabian symbol set would be ~10,000+ lines.
 * Example implementation provided for reference.
 */
export const SABIAN_SYMBOLS_INFO = {
  creators: 'Marc Edmund Jones and Elsie Wheeler',
  year: 1925,
  location: 'San Diego, California',
  method: 'Clairvoyant symbol assignment',
  total: 360,
  structure: '12 signs × 30 degrees = 360 unique symbols',
  
  example: {
    degree: 1,
    sign: 'Aries',
    symbol: 'A woman rises out of the water, a seal rises and embraces her',
    keyword: 'Emergence',
    meaning: 'Emergence of new consciousness, reconciliation with instinctive nature',
    reference: 'Aries 1° in Sabian Symbols',
  },
  
  note: 'Full 360-symbol database can be added if desired. Each symbol has: image, keyword, interpretation, and degree position.',
  
  usage: 'Given birth chart degree position, return corresponding symbolic meaning',
  
  scientificStatus: 'Esoteric symbol system, not scientifically validated',
  culturalStatus: 'Widely referenced in modern astrological practice',
};

// ============================================================================
// PLANETARY CORRESPONDENCES
// ============================================================================

/**
 * Traditional planetary qualities
 * 
 * From classical astrology (Ptolemy, Vettius Valens)
 * and modern interpretations
 */
export const PLANETARY_CORRESPONDENCES = {
  Sun: {
    symbol: '☉',
    element: 'Fire',
    metal: 'Gold',
    day: 'Sunday',
    color: '#FDB813',
    number: 1,
    qualities: ['Vitality', 'Ego', 'Consciousness', 'Father', 'Authority'],
    anatomy: 'Heart, spine, vitality',
    exaltedIn: 'Aries',
    rulership: 'Leo',
    detrimentIn: 'Aquarius',
    fallIn: 'Libra',
  },
  
  Moon: {
    symbol: '☽',
    element: 'Water',
    metal: 'Silver',
    day: 'Monday',
    color: '#C0C0C0',
    number: 2,
    qualities: ['Emotion', 'Intuition', 'Nurturing', 'Mother', 'Subconscious'],
    anatomy: 'Stomach, breasts, fluids',
    exaltedIn: 'Taurus',
    rulership: 'Cancer',
    detrimentIn: 'Capricorn',
    fallIn: 'Scorpio',
    cycle: '29.53059 days (synodic month)',
  },
  
  Mercury: {
    symbol: '☿',
    element: 'Air',
    metal: 'Mercury (quicksilver)',
    day: 'Wednesday',
    color: '#87CEEB',
    number: 5,
    qualities: ['Communication', 'Intellect', 'Reasoning', 'Commerce', 'Travel'],
    anatomy: 'Nervous system, hands, lungs',
    exaltedIn: 'Virgo',
    rulership: 'Gemini, Virgo',
    detrimentIn: 'Sagittarius, Pisces',
    fallIn: 'Pisces',
  },
  
  Venus: {
    symbol: '♀',
    element: 'Earth/Air',
    metal: 'Copper',
    day: 'Friday',
    color: '#FF69B4',
    number: 6,
    qualities: ['Love', 'Beauty', 'Harmony', 'Art', 'Pleasure'],
    anatomy: 'Throat, kidneys, reproductive system',
    exaltedIn: 'Pisces',
    rulership: 'Taurus, Libra',
    detrimentIn: 'Scorpio, Aries',
    fallIn: 'Virgo',
  },
  
  Mars: {
    symbol: '♂',
    element: 'Fire',
    metal: 'Iron',
    day: 'Tuesday',
    color: '#DC143C',
    number: 9,
    qualities: ['Action', 'Courage', 'Desire', 'Conflict', 'Energy'],
    anatomy: 'Muscles, blood, adrenal glands',
    exaltedIn: 'Capricorn',
    rulership: 'Aries, Scorpio (traditional)',
    detrimentIn: 'Libra, Taurus',
    fallIn: 'Cancer',
  },
  
  Jupiter: {
    symbol: '♃',
    element: 'Fire',
    metal: 'Tin',
    day: 'Thursday',
    color: '#4169E1',
    number: 3,
    qualities: ['Expansion', 'Wisdom', 'Growth', 'Abundance', 'Philosophy'],
    anatomy: 'Liver, hips, thighs',
    exaltedIn: 'Cancer',
    rulership: 'Sagittarius, Pisces (traditional)',
    detrimentIn: 'Gemini, Virgo',
    fallIn: 'Capricorn',
  },
  
  Saturn: {
    symbol: '♄',
    element: 'Earth',
    metal: 'Lead',
    day: 'Saturday',
    color: '#2F4F4F',
    number: 8,
    qualities: ['Structure', 'Discipline', 'Time', 'Limitation', 'Responsibility'],
    anatomy: 'Bones, teeth, skin, joints',
    exaltedIn: 'Libra',
    rulership: 'Capricorn, Aquarius (traditional)',
    detrimentIn: 'Cancer, Leo',
    fallIn: 'Aries',
  },
  
  Uranus: {
    symbol: '♅',
    element: 'Air',
    metal: 'Uranium',
    day: 'None (modern planet)',
    color: '#00CED1',
    number: 4,
    qualities: ['Innovation', 'Revolution', 'Freedom', 'Eccentricity', 'Change'],
    anatomy: 'Nervous system, ankles',
    exaltedIn: 'Scorpio (modern)',
    rulership: 'Aquarius (modern)',
    detrimentIn: 'Leo',
    fallIn: 'Taurus',
  },
  
  Neptune: {
    symbol: '♆',
    element: 'Water',
    metal: 'Neptunium',
    day: 'None (modern planet)',
    color: '#9370DB',
    number: 7,
    qualities: ['Illusion', 'Spirituality', 'Dreams', 'Compassion', 'Mysticism'],
    anatomy: 'Pineal gland, feet, immune system',
    exaltedIn: 'Leo (modern), Cancer (traditional)',
    rulership: 'Pisces (modern)',
    detrimentIn: 'Virgo',
    fallIn: 'Capricorn (modern)',
  },
  
  Pluto: {
    symbol: '♇',
    element: 'Water',
    metal: 'Plutonium',
    day: 'None (modern planet)',
    color: '#8B0000',
    number: 0,
    qualities: ['Transformation', 'Power', 'Death/Rebirth', 'Intensity', 'Hidden'],
    anatomy: 'Reproductive organs, elimination system',
    exaltedIn: 'Aries (modern), Aquarius (alternative)',
    rulership: 'Scorpio (modern)',
    detrimentIn: 'Taurus',
    fallIn: 'Libra',
  },
} as const;

// ============================================================================
// VEDIC ASTROLOGY (JYOTISH)
// ============================================================================

/**
 * Vedic Astrology Basics
 * 
 * Sanskrit: Jyotiṣa (ज्योतिष) = "Science of Light"
 * 
 * Key differences from Western:
 * - Uses Sidereal zodiac (based on actual constellations)
 * - Currently about 24° behind Tropical zodiac due to precession
 * - Nakshatras (27 lunar mansions) used alongside 12 signs
 * - Emphasis on Moon sign over Sun sign
 * - Uses whole-sign house system
 * 
 * Ayanamsa (अयनांश): Correction factor for precession
 * Most common: Lahiri ayanamsa ≈ 24°08' (as of 2024)
 * 
 * Source: Brihat Parashara Hora Shastra (foundational Vedic text)
 */
export const VEDIC_ASTROLOGY = {
  name: {
    sanskrit: 'ज्योतिष (Jyotiṣa)',
    meaning: 'Science of Light',
  },
  
  zodiacType: 'Sidereal (constellation-based)',
  
  ayanamsa: {
    name: 'Lahiri Ayanamsa',
    value2024: 24.14, // degrees
    meaning: 'Correction for precession of equinoxes',
    formula: 'Tropical longitude - Ayanamsa = Sidereal longitude',
  },
  
  nakshatras: {
    total: 27,
    meaning: 'Lunar mansions',
    eachSpans: '13°20\' (360° / 27)',
    importance: 'Primary division in Vedic astrology',
    examples: [
      { name: 'Ashwini (अश्विनी)', degrees: '0°-13°20\' Aries', deity: 'Ashwini Kumaras', meaning: 'Horse woman' },
      { name: 'Bharani (भरणी)', degrees: '13°20\'-26°40\' Aries', deity: 'Yama', meaning: 'She who bears' },
      { name: 'Krittika (कृत्तिका)', degrees: '26°40\' Aries-10° Taurus', deity: 'Agni', meaning: 'The cutter' },
      // ... all 27 nakshatras
    ],
  },
  
  note: 'Full nakshatra system can be implemented if desired',
  source: 'Brihat Parashara Hora Shastra',
};

// ============================================================================
// SCIENTIFIC DISCLAIMER
// ============================================================================

export const ASTROLOGY_DISCLAIMER = {
  astronomicalCalculations: {
    status: 'Scientifically verified',
    validation: 'Positions of Sun, Moon, planets can be calculated and observed',
    sources: ['NASA JPL', 'IAU standards', 'VSOP87 theory'],
    accuracy: 'High precision achievable with proper ephemeris data',
  },
  
  astrologicalInterpretations: {
    status: 'Not scientifically validated',
    culturalSignificance: 'Rich historical and cultural traditions',
    personalValue: 'Many find symbolic and psychological value',
    research: [
      {
        study: 'Carlson double-blind test (1985)',
        journal: 'Nature',
        result: 'No evidence for astrological accuracy above chance',
      },
      {
        note: 'Multiple studies have found no statistical evidence for core astrological claims',
      },
    ],
  },
  
  appropriateUses: [
    'Symbolic language for psychology',
    'Cultural and historical study',
    'Personal reflection tool',
    'Archetypal exploration',
    'Timing marker (moon phases for planting, etc.)',
  ],
  
  inappropriateUses: [
    'Making major life decisions based solely on astrology',
    'Judging others by sun sign',
    'Ignoring evidence and reason',
    'Replacing medical or professional advice',
  ],
  
  honestStatement: 'Astrology is a rich symbolic and cultural tradition with thousands of years of history. While astronomical calculations are scientifically verifiable, astrological interpretations are not validated by scientific research. We present this information for cultural, historical, and personal exploration, not as scientific fact.',
} as const;

// ============================================================================
// EXPORT ALL
// ============================================================================

export default {
  ZODIAC_SIGNS,
  calculateJulianDay,
  calculateSunPosition,
  getZodiacSign,
  getCurrentAstrologicalData,
  calculateMoonPhase,
  VEDIC_ASTROLOGY,
  SABIAN_SYMBOLS_INFO,
  PLANETARY_CORRESPONDENCES,
  ASTROLOGY_DISCLAIMER,
};

